// Full history of a single restaurant ORDER by id — summary, items, payments
// (CR/DR), gateway payments, invoices, table/room changes. Read-only.
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import config from '../../config';

const token = sessionStorage.getItem('token');
const auth = { Authorization: `Bearer ${token}` };
const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;
const dt = (d) => (d ? new Date(d).toLocaleString('en-IN') : '—');

function Section({ title, children }) {
  return (
    <Paper elevation={2} sx={{ borderRadius: 3, p: 2, mb: 2 }}>
      <Typography fontWeight={800} sx={{ mb: 1, color: '#0a2540' }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      {children}
    </Paper>
  );
}

function Grid2(rows) {
  return (
    <Grid container spacing={1}>
      {rows.map(([k, v]) => (
        <Grid item xs={6} md={3} key={k}>
          <Typography variant='caption' color='text.secondary'>
            {k}
          </Typography>
          <Typography fontWeight={600}>{v ?? '—'}</Typography>
        </Grid>
      ))}
    </Grid>
  );
}

export default function OrderHistory() {
  const [orderId, setOrderId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    const id = Number(orderId);
    if (!id) return toast.info('Enter an order id');
    setLoading(true);
    setData(null);
    fetch(`${config.API_BASE_URL}/history/order/${id}`, { headers: auth })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setData(d))
      .catch(() => toast.error('Order not found'))
      .finally(() => setLoading(false));
  };

  const o = data?.order;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 3,
          color: '#fff',
          background:
            'linear-gradient(135deg,#0b1120 0%,#0f172a 40%,#1e3a8a 100%)',
        }}>
        <Typography variant='h5' fontWeight={800} sx={{ color: '#fff' }}>
          🧾 Order History
        </Typography>
        <Typography variant='body2' sx={{ opacity: 0.85, color: '#fff' }}>
          Enter an order id to see everything that happened to it
        </Typography>
      </Paper>

      <Paper elevation={2} sx={{ borderRadius: 3, p: 2, mb: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size='small'
          type='number'
          label='Order ID'
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
          sx={{ minWidth: 200 }}
        />
        <Button variant='contained' startIcon={<SearchIcon />} onClick={load}>
          Load History
        </Button>
      </Paper>

      {loading && (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {o && (
        <>
          <Section title={`Order #${o.id} — ${o.orderType || 'DineIn'}`}>
            {Grid2([
              ['Table', o.tableno],
              ['Booking Id', o.bookingId || '—'],
              ['Created', dt(o.createDate)],
              ['Invoice No', o.invNumber || '—'],
              ['Price', fmt(o.price)],
              ['Discount', fmt(o.dicountamount)],
              ['GST', fmt(o.gstAmount)],
              ['Final Price', fmt(o.finalPrice)],
              ['Customer', o.customerName || '—'],
              ['Mobile', o.customerMobile || '—'],
              ['Rider', o.riderName || '—'],
              ['Delivery', o.deliveryStatus || '—'],
            ])}
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip size='small' color={o.isPaid ? 'success' : 'default'} label={o.isPaid ? 'Paid' : 'Unpaid'} />
              <Chip size='small' color={o.isInvoice ? 'info' : 'default'} label={o.isInvoice ? 'Invoiced' : 'No invoice'} />
              {o.isCancel && <Chip size='small' color='error' label='Cancelled' />}
              {o.isSplitBill && <Chip size='small' color='warning' label='Split bill' />}
            </Box>
          </Section>

          <Section title={`Items (${data.items?.length || 0})`}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {['Item', 'Variant', 'Qty', 'Price', 'Final', 'KOT', 'Status', 'When'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.items || []).map((it) => (
                  <TableRow key={it.id}>
                    <TableCell>{it.name}</TableCell>
                    <TableCell>{it.variant || '—'}</TableCell>
                    <TableCell>{it.qty}</TableCell>
                    <TableCell>{fmt(it.price)}</TableCell>
                    <TableCell>{fmt(it.finalPrice)}</TableCell>
                    <TableCell>{it.kot || '—'}</TableCell>
                    <TableCell>
                      {!it.activestatus ? (
                        <Chip size='small' color='error' label='Void' />
                      ) : it.isDeleverd ? (
                        <Chip size='small' color='success' label='Served' />
                      ) : (
                        <Chip size='small' label='Active' />
                      )}
                      {it.cancelRemark ? ` (${it.cancelRemark})` : ''}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dt(it.createDate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>

          <Section title={`Payments — CR/DR  (CR ${fmt(data.totals?.totalCr)} · DR ${fmt(data.totals?.totalDr)} · Net ${fmt(data.totals?.balance)})`}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {['Date', 'Type', 'Amount', 'Mode', 'Ref/Card', 'Remark', 'By'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.payments || []).length === 0 ? (
                  <TableRow><TableCell colSpan={7} align='center' sx={{ color: '#94a3b8' }}>No payments.</TableCell></TableRow>
                ) : (
                  data.payments.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dt(p.date)}</TableCell>
                      <TableCell>
                        <Chip size='small' color={p.type === 'CR' ? 'success' : 'warning'} label={p.type || '—'} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{fmt(p.amount)}</TableCell>
                      <TableCell>{p.mode || '—'}</TableCell>
                      <TableCell>{[p.refNo, p.cardType].filter(Boolean).join(' / ') || '—'}</TableCell>
                      <TableCell>{p.remark || '—'}</TableCell>
                      <TableCell>{p.userName || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Section>

          {(data.gateway || []).length > 0 && (
            <Section title={`Online Gateway Payments (${data.gateway.length})`}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    {['Date', 'Amount', 'Status', 'Mode', 'Tracking', 'Bank Ref'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.gateway.map((g) => (
                    <TableRow key={g.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dt(g.createdDate)}</TableCell>
                      <TableCell>{fmt(g.amount)}</TableCell>
                      <TableCell>
                        <Chip size='small' color={g.status === 'Success' ? 'success' : 'default'} label={g.status} />
                      </TableCell>
                      <TableCell>{g.paymentMode || '—'}</TableCell>
                      <TableCell>{g.trackingId || '—'}</TableCell>
                      <TableCell>{g.bankRefNo || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          )}

          {(data.invoices || []).length > 0 && (
            <Section title={`Invoices (${data.invoices.length})`}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    {['Invoice No', 'Price', 'GST', 'Final', 'Date'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.invoices.map((iv, i) => (
                    <TableRow key={i}>
                      <TableCell>{iv.invoiceNumber}</TableCell>
                      <TableCell>{fmt(iv.price)}</TableCell>
                      <TableCell>{fmt(iv.gstAmount)}</TableCell>
                      <TableCell>{fmt(iv.finalPrice)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dt(iv.createDate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          )}

          {(data.tableChanges || []).length > 0 && (
            <Section title={`Table / Room Changes (${data.tableChanges.length})`}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    {['From', 'To', 'By', 'When'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.tableChanges.map((t, i) => (
                    <TableRow key={i}>
                      <TableCell>{t.oldTable}</TableCell>
                      <TableCell>{t.newTable}</TableCell>
                      <TableCell>{t.changedByName || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dt(t.changedOn)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          )}
        </>
      )}
    </Box>
  );
}
